import { useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { FormikProvider, useFormik } from 'formik'
import { DateTime } from 'luxon'
import * as yup from 'yup'
import { useBooleanFlagValue } from '@openfeature/react-sdk'
import { FEATURE_FLAG_KEYS } from '@/features/open-feature'
import {
  useNavigateWithPersistParams,
  useReselector,
  useScrollRef,
} from '@/utils/sharedHooks'
import { useAppDispatch } from '@/utils/typeHelpers'
import { DATE_FORMATS, DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import {
  ReferralStatus,
  updateUserReferralStatusIfExists,
} from '@/features/user/signup/about-you/referrals.slice'
import { selectApproximatePracticeStartDate } from '@/selectors/catchupBookkeeping.selectors'
import { updateFinancialProfile } from '@/actions/financialProfileActions'
import {
  getCurrentUser,
  getFinancialProfile,
  getFreeTrialStatus,
} from '@/selectors/user.selectors'

import { useSignupPageUpdate } from '@/services/onboardingService'

import { SIGNUP_PATHS, C_CORP_TAX_ENTITY, isFreeTrialEnabled } from '../helpers'
import { useAnalyticsView } from '@/features/amplitude'
import {
  BecomeSCorpToolTip,
  CommingledBankingToolTip,
  EmployeeCountToolTip,
  InternationalTaxToolTip,
  MultipleOwnersToolTip,
  SeperateBankingToolTip,
} from './ToolTips'

import { getFieldName, makeReqStringSchema } from '@/base'

import { TypographyStyles } from '@/styles'
import { Button, Form, Image, RadioGroupItem, Separator, Text } from '@/ui'
import { FreeTrialSignupBanner } from '../FreeTrialSignupBanner'
import { SignupHeader } from '../SignupHeader'
import { QuestionRow } from './QuestionRow'
import { PracticeType } from '@/constants/onboardingConstants'

// TODO: Migrate to V2
import { fetchAllAnnualTaxDetailsIfNeeded } from '@/featuresV1/Admin/AnnualTaxDetails/annualTaxDetails.slice'
import { TAX_ENTITY_TYPES } from '@/featuresV1/Taxes/taxConstants'

/** Including self (so add 1) */
enum NumberOfEmployees {
  justMe = '1',
  twoToTen = '2-10',
  tenOrMore = '10+',
}
const trueOrFalseSchema = yup.string().nullable().oneOf(['true', 'false'])

export const YourPractice = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigateWithPersistParams()
  const pageView = useAnalyticsView()
  const currentUser = useReselector(getCurrentUser)
  const financialProfile = useReselector(getFinancialProfile)
  const { scrollRef, scrollToRef } = useScrollRef({ autoScroll: true })

  const approximatePracticeStartDate = useReselector(
    selectApproximatePracticeStartDate
  )

  const [searchParams] = useSearchParams()
  const basicPlanSearchParamValue = searchParams.get('subscription')
  const useBasicTypePlan =
    basicPlanSearchParamValue === 'new' || basicPlanSearchParamValue === 'basic'
  const userFreeTrialStatus = useReselector(getFreeTrialStatus)
  const freeTrialEnabled = isFreeTrialEnabled(
    searchParams.get('trial'),
    userFreeTrialStatus,
    useBasicTypePlan
  )

  const enableStarterPlans = useBooleanFlagValue(
    FEATURE_FLAG_KEYS.enableStarterPlans,
    false
  )
  useEffect(() => {
    dispatch(fetchAllAnnualTaxDetailsIfNeeded())
  }, [dispatch])

  const { email } = currentUser || {}
  useEffect(() => {
    if (email) {
      updateUserReferralStatusIfExists({
        recipientEmail: email,
        status: ReferralStatus.account_created,
      })(dispatch)
    }
  }, [email, dispatch])

  useEffect(() => {
    pageView('sign up about your practice')
  }, [pageView])

  useEffect(() => {
    scrollToRef()
  }, [scrollToRef])

  useSignupPageUpdate(SIGNUP_PATHS.aboutYourPractice)

  const formik = useFormik({
    initialValues: {
      taxEntityType: financialProfile?.taxEntityType as
        | TAX_ENTITY_TYPES
        | 'other',
      businessEntity: financialProfile?.businessEntity || undefined,
      accountsSeparate: financialProfile?.accountsSeparate?.toString(),
      willingAccountsSeparate: financialProfile?.willingAccountsSeparate,
      multipleOwners: null as boolean | null,
      over10Employees: null as boolean | null,
      scorpEntireYear: null as boolean | null,
      fileInternationalTaxes: financialProfile?.fileInternationalTaxes,
      approximatePracticeStartDate:
        approximatePracticeStartDate?.toFormat(DATE_FORMATS_LUXON.YEAR_MONTH) ||
        '',
      numberOfEmployees:
        financialProfile?.practiceType === PracticeType.solo
          ? NumberOfEmployees.justMe
          : financialProfile?.practiceType === PracticeType.group
            ? NumberOfEmployees.twoToTen
            : '',
    },
    validateOnMount: true,
    onSubmit: async ({
      taxEntityType,
      businessEntity,
      accountsSeparate,
      willingAccountsSeparate,
      multipleOwners,
      over10Employees,
      fileInternationalTaxes,
      approximatePracticeStartDate,
      scorpEntireYear,
      numberOfEmployees,
    }) => {
      const otherDisqualifications =
        taxEntityType === 'other' &&
        businessEntity &&
        ['llc_single', 'c_corp', 'other'].includes(businessEntity)

      const navToConsult =
        businessEntity === 'other' &&
        ['form_1040', 'form_1120_s'].includes(taxEntityType)

      const isValidTaxEntityType = taxEntityType !== C_CORP_TAX_ENTITY
      const isValidAccountsSeparation =
        accountsSeparate || willingAccountsSeparate
      const isValidOwnerCount = multipleOwners?.toString() !== 'true'
      const isValidTaxNtionalities =
        fileInternationalTaxes?.toString() !== 'true'
      const isValidScorpEntireYear = scorpEntireYear !== false

      const isQualifiedUser =
        isValidTaxEntityType &&
        isValidAccountsSeparation &&
        isValidOwnerCount &&
        isValidTaxNtionalities &&
        isValidScorpEntireYear &&
        !otherDisqualifications

      if (!isQualifiedUser) {
        navigate(SIGNUP_PATHS.disqualified, {
          hasInternationalTaxes: Boolean(fileInternationalTaxes).toString(),
          multipleOwners: Boolean(multipleOwners).toString(),
          ccorp: Boolean(taxEntityType === C_CORP_TAX_ENTITY).toString(),
          sharedAccounts: Boolean(
            !accountsSeparate && !willingAccountsSeparate
          ).toString(),
          scorpEntireYear: Boolean(scorpEntireYear).toString(),
          otherSelection: Boolean(otherDisqualifications).toString(),
        })
      } else if (
        over10Employees?.toString() === 'true' ||
        numberOfEmployees === NumberOfEmployees.tenOrMore
      ) {
        navigate(SIGNUP_PATHS.waitlist)
      } else if (navToConsult) {
        navigate(SIGNUP_PATHS.consult)
      } else {
        const taxEntityValue =
          taxEntityType === 'other'
            ? TAX_ENTITY_TYPES.schedule_c
            : taxEntityType

        await updateFinancialProfile(financialProfile?.id, {
          accountsSeparate: accountsSeparate === 'true' ? true : false,
          willingAccountsSeparate,
          businessEntity,
          taxEntityType: taxEntityValue,
          fileInternationalTaxes,
          approximatePracticeStartDate,
          practiceType: enableStarterPlans
            ? numberOfEmployees === NumberOfEmployees.justMe
              ? PracticeType.solo
              : PracticeType.group
            : undefined,
          practiceSize: enableStarterPlans ? numberOfEmployees : undefined,
        })(dispatch)

        navigate(SIGNUP_PATHS.practiceJourney)
      }
    },
  })

  const { values, submitForm, isSubmitting } = formik

  const BUSINESS_ENTITY_OPTIONS = {
    sprop: 'Sole Proprietor',
    llc_single: 'LLC',
    pllc_single: 'PLLC',
    c_corp: 'Corporation',
    other: 'Other',
  } as const

  const TAXED_AS_TYPE = {
    form_1040: 'Sole Proprietor',
    form_1120_s: 'S Corporation',
    form_1120: 'C Corporation',
    other: 'Other',
  } as const

  return (
    <div className="container mx-auto px-4 pt-16 pb-4">
      <FormikProvider value={formik}>
        <div className="flex flex-col gap-6">
          <span ref={scrollRef} />
          <SignupHeader currentStep={1} />
          {freeTrialEnabled && <FreeTrialSignupBanner />}

          <Text variant={TypographyStyles.H1}>
            Tell us a little bit more about your practice
          </Text>

          <Text variant={TypographyStyles.BODY_MD}>
            To serve you accurately, we need a clear picture of your practice —
            please answer each question carefully.
          </Text>
          <div className="flex flex-row">
            <div className="w-2/3 flex flex-col gap-2">
              <Form.RadioGroup
                name={getFieldName<typeof values>('businessEntity')}
                schema={makeReqStringSchema({})}
                legend="How is your practice set up?"
                required
              >
                {Object.entries(BUSINESS_ENTITY_OPTIONS).map(
                  ([value, label]) => (
                    <RadioGroupItem key={value} value={value} label={label} />
                  )
                )}
              </Form.RadioGroup>

              <Form.RadioGroup
                name={getFieldName<typeof values>('taxEntityType')}
                legend="Your practice is taxed as a... "
                schema={makeReqStringSchema({})}
                className="flex gap-4"
              >
                {Object.entries(TAXED_AS_TYPE).map(([value, label]) => (
                  <RadioGroupItem key={value} value={value} label={label} />
                ))}
              </Form.RadioGroup>

              {values.businessEntity === 'c_corp' &&
                values.taxEntityType === 'form_1120_s' && (
                  <QuestionRow
                    name={getFieldName<typeof values>('scorpEntireYear')}
                    label={`Has your practice been taxed as an S Corporation for the entirety of ${DateTime.now().year}?`}
                    tooltip={<BecomeSCorpToolTip />}
                  />
                )}

              <Form.DateInput
                name={getFieldName<typeof values>(
                  'approximatePracticeStartDate'
                )}
                label="When did you start your practice?"
                placeholder="Select month"
                dateFormat={DATE_FORMATS.YEAR_MONTH}
                showMonthYearPicker
                maxDate={new Date()}
              />

              {enableStarterPlans && (
                <Form.RadioGroup
                  name={getFieldName<typeof values>('numberOfEmployees')}
                  schema={makeReqStringSchema({})}
                  legend="How many employees are in your practice?"
                  className="flex gap-4"
                >
                  <RadioGroupItem
                    value={NumberOfEmployees.justMe}
                    label="Just me"
                  />
                  <RadioGroupItem
                    value={NumberOfEmployees.twoToTen}
                    label="I have between 1-9 employees and contractors."
                  />
                  <RadioGroupItem
                    value={NumberOfEmployees.tenOrMore}
                    label="I have 10 or more employees and contractors."
                  />
                </Form.RadioGroup>
              )}

              <Text
                variant={TypographyStyles.DISPLAY_XS}
                className="mt-4 flex items-center gap-1"
              >
                Do any of these situations apply to you?
                <CommingledBankingToolTip />
              </Text>

              <Form.RadioGroup
                className="min-h-fit flex gap-4"
                label={'How do you pay for business expenses?'}
                name={getFieldName<typeof values>('accountsSeparate')}
                schema={makeReqStringSchema({})}
              >
                <RadioGroupItem
                  value="true"
                  id={`${name}-yes`}
                  label="Business bank account"
                />
                <RadioGroupItem
                  value="false"
                  id={`${name}-no`}
                  label="Personal bank account"
                />
              </Form.RadioGroup>

              {values.accountsSeparate === 'false' && (
                <QuestionRow
                  name={getFieldName<typeof values>('willingAccountsSeparate')}
                  label="Are you willing to separate your personal and business banking activity?"
                  tooltip={<SeperateBankingToolTip />}
                  schema={trueOrFalseSchema.when(
                    'accountsSeparate',
                    ([accountsSeparate], schema) =>
                      accountsSeparate === 'false' ? schema.required() : schema
                  )}
                />
              )}

              <QuestionRow
                name={getFieldName<typeof values>('multipleOwners')}
                label="Does your practice have multiple owners (other than a current spouse)?"
                tooltip={<MultipleOwnersToolTip />}
              />

              {!enableStarterPlans && (
                <QuestionRow
                  name={getFieldName<typeof values>('over10Employees')}
                  label="Does your practice have more than 10 employees?"
                  tooltip={<EmployeeCountToolTip />}
                />
              )}

              <QuestionRow
                name={getFieldName<typeof values>('fileInternationalTaxes')}
                label="Does your practice need to pay international taxes?"
                tooltip={<InternationalTaxToolTip />}
              />
            </div>
            <div className="w-1/3">
              <Image
                alt="couch"
                src="https://heard-images.s3.amazonaws.com/assets/couch.svg"
              />
            </div>
          </div>
          <Separator />
          <div className="flex justify-end">
            <Button
              onClick={submitForm}
              disabled={isSubmitting}
              id="btn-signup-about-practice"
              loading={isSubmitting}
              className="max-w-[200px]"
            >
              Continue
            </Button>
          </div>
        </div>
      </FormikProvider>
    </div>
  )
}
